import React, { Fragment, memo, useRef } from 'react';
import FirstColCellContent from '../FirstColCellContent';
import {
  BodyContainer,
  Cell,
  CornerCell,
  FirstColCell,
  HeaderCell,
  HeaderContainer,
} from './components';
import Interval from '../Interval';
import StickySyncedScrollLayout from '../../../../../shared/ui/StickySyncedScrollLayout';
import { DayActions, DeskActions, IntervalActions } from './actionsComponents';
import { useUserAcceptanceRole } from '../../../../../shared/hooks/userAcceptanceRole';
import { useHeaderSize } from '../services/headerSizeCalculator';
import styles from './index.module.scss';

const Calendar = ({ page, mode, data, calendarManager }) => {
  const role = useUserAcceptanceRole();

  const prevDataRef = useRef();
  const prevManagerRef = useRef();

  if (
    prevDataRef.current !== data ||
    prevManagerRef.current !== calendarManager
  ) {
    calendarManager.setSelectedPeriodInfo(data);
    prevDataRef.current = data;
    prevManagerRef.current = calendarManager;
  }

  const dates = calendarManager.getDates(mode, page);
  const desks = calendarManager.getDesks();

  const containerStyle = {
    '--acceptance-calendar-container-length': desks.length || 1,
  };

  return (
    <Container header={<Header style={containerStyle} desks={desks} />}>
      <BodyContainer style={containerStyle}>
        {dates.map((day, index) => {
          const [date, isoDateStr] = day;

          const gridRow = index + 1;

          return (
            <Fragment key={isoDateStr}>
              <FirstColCell style={{ gridRow }}>
                <FirstColCellContent date={date}>
                  <DayActions
                    calendarManager={calendarManager}
                    dayData={day}
                    role={role}
                  />
                </FirstColCellContent>
              </FirstColCell>

              {desks.map((desk, deskIndex) => {
                const intervals = calendarManager.getDeskDayIntervals(
                  desk.id,
                  isoDateStr
                );

                return (
                  <Cell
                    key={`${desk.id}-${isoDateStr}`}
                    isOdd={isOdd(deskIndex + 1)}
                    style={{ gridRow, gridColumn: deskIndex + 2 }}
                    action={
                      role.isDirector && (
                        <DeskActions
                          calendarManager={calendarManager}
                          dayData={day}
                          deskId={desk.id}
                        />
                      )
                    }
                  >
                    {intervals.map((interval) => {
                      return (
                        <Interval
                          key={interval.id}
                          interval={interval}
                          action={
                            !role.isReader && (
                              <IntervalActions
                                calendarManager={calendarManager}
                                dayData={day}
                                interval={interval}
                                role={role}
                              />
                            )
                          }
                        />
                      );
                    })}
                  </Cell>
                );
              })}
            </Fragment>
          );
        })}
      </BodyContainer>
    </Container>
  );
};

export default memo(Calendar);

function Container({ children, header }) {
  const headerHeight = useHeaderSize();

  return (
    <StickySyncedScrollLayout
      topOffset={headerHeight}
      className={styles.root}
      header={header}
    >
      {children}
    </StickySyncedScrollLayout>
  );
}

function Header({ style, desks }) {
  return (
    <HeaderContainer style={style}>
      <CornerCell />

      {desks.map((desk, index) => (
        <HeaderCell key={desk.id} style={{ gridColumn: index + 2 }}>
          {desk.name}
        </HeaderCell>
      ))}
    </HeaderContainer>
  );
}

function isOdd(n) {
  return n % 2 !== 0;
}
